import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import VideoCard from "@/components/base/VideoCard";
import FilterBar, { type FilterState } from "@/components/feature/FilterBar";
import Pagination from "@/components/feature/Pagination";
import { usePageMeta } from "@/hooks/usePageMeta";
import { videos } from "@/mocks/videos";
import { filterVideos, paginate, sortVideos, totalPages } from "@/utils/videoFilter";

const PAGE_SIZE = 10;

function readFilters(params: URLSearchParams): FilterState {
  return {
    type: params.get("type") || "全部",
    genre: params.get("genre") || "全部",
    region: params.get("region") || "全部",
    year: params.get("year") || "全部",
    sort: params.get("sort") || "热度",
  };
}

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = readFilters(searchParams);
  const page = Math.max(1, Number(searchParams.get("page") || "1"));

  const categoryLabel = filters.type === "全部" ? "影视分类" : filters.type;

  usePageMeta(
    `${categoryLabel} - 芒果视频`,
    `芒果视频${categoryLabel}频道，汇集${filters.region === "全部" ? "各地" : filters.region}优质${filters.genre === "全部" ? "" : filters.genre}内容，${
      filters.year === "全部" ? "全年份" : filters.year + "年"
    }热播作品一键筛选，畅享精彩影视资源。`,
    `芒果视频,${categoryLabel},${filters.genre === "全部" ? "影视筛选" : filters.genre},${filters.region === "全部" ? "全部地区" : filters.region},在线观看`
  );

  const result = useMemo(() => {
    const filtered = filterVideos(videos, filters);
    return sortVideos(filtered, filters.sort);
  }, [filters]);

  const pages = totalPages(result.length, PAGE_SIZE);
  const currentPage = Math.min(page, pages);
  const list = paginate(result, currentPage, PAGE_SIZE);

  const updateFilter = (key: keyof FilterState, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === "全部" || (key === "sort" && value === "热度")) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    next.delete("page");
    setSearchParams(next);
  };

  const changePage = (target: number) => {
    const next = new URLSearchParams(searchParams);
    if (target <= 1) {
      next.delete("page");
    } else {
      next.set("page", String(target));
    }
    setSearchParams(next);
  };

  const resetFilters = () => setSearchParams(new URLSearchParams());

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-6 md:py-8 lg:px-10">
      <nav className="flex items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑导航">
        <Link to="/" className="transition-colors hover:text-primary-400">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-700">影视分类</span>
        {filters.type !== "全部" && (
          <>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-primary-400">{filters.type}</span>
          </>
        )}
      </nav>

      <div className="mt-4">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          {categoryLabel}
        </h1>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
          {filters.type === "全部"
            ? "在这里按类型、标签、地区与年份自由筛选，找到你想看的内容。"
            : `浏览全部${filters.type}内容，通过下方筛选快速缩小范围。`}
        </p>
      </div>

      <div className="mt-5">
        <FilterBar filters={filters} onChange={updateFilter} />
      </div>

      <div className="mt-5 flex items-center justify-between">
        <p className="text-sm text-foreground-600">
          共找到 <span className="font-semibold text-primary-400">{result.length}</span> 部内容
        </p>
        <button
          type="button"
          onClick={resetFilters}
          className="flex items-center gap-1 whitespace-nowrap text-xs text-foreground-600 transition-colors hover:text-primary-400"
        >
          <i className="ri-refresh-line"></i>
          重置筛选
        </button>
      </div>

      {list.length > 0 ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4 xl:grid-cols-5">
            {list.map((item) => (
              <VideoCard key={item.id} item={item} />
            ))}
          </div>
          <Pagination page={currentPage} total={pages} onChange={changePage} />
        </>
      ) : (
        <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-emotion-sad-line text-2xl"></i>
          </span>
          <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-900">没有找到匹配的内容</h2>
          <p className="mt-2 text-sm text-foreground-600">试试放宽筛选条件，或者直接查看全部内容。</p>
          <button
            type="button"
            onClick={resetFilters}
            className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            重置筛选条件
          </button>
        </div>
      )}
    </div>
  );
}